import { useState, useEffect } from 'react';
import {  useSuperQuery } from '@vadiun/react-hooks-legacy';
import { useViajesTraslado } from 'app/viaje/services';
import { TransferReservationBaseFiltersFormType } from '../../viajeTraslado/components/ViajeTrasladoBaseFilters';
import {
  OpenedTravelColumnVisibility,
  getDefaultFilters,
  getDefaultColumnVisibility,
  getDefaultSort,
  loadStateFromLocalStorage,
  saveStateToLocalStorage
} from '../utils/openedStorage';
import { useNotificationsContext } from 'shared/context/notificationContext';

// Re-exportar para mantener compatibilidad
export {
  openedTravelColumnLabels,
  getDefaultFilters
} from '../utils/openedStorage';

export const useOpenedTravel = () => {
  const tripRepository = useViajesTraslado();
  const { shouldOpenedReload, resetOpenedReloadFlag } =
    useNotificationsContext();

  // Un solo estado que engloba todo
  const [state, setState] = useState(loadStateFromLocalStorage);

  // Sincronizar con localStorage
  useEffect(() => {
    saveStateToLocalStorage(state);
  }, [state]);

  // Usar valores primitivos para las dependencias de useSuperQuery
  const getOpenedTravel = useSuperQuery(
    () =>
      tripRepository.getAllOpen(
        state.page,
        state.rowsPerPage,
        state.sort.sortBy,
        state.sort.sortDir,
        state.filters
      ),
    { showSpinner: true },
    [
      state.page,
      state.rowsPerPage,
      state.sort.sortBy,
      state.sort.sortDir,
      JSON.stringify(state.filters)
    ]
  );

  useEffect(() => {
    if (shouldOpenedReload) {
      getOpenedTravel.reload();
      resetOpenedReloadFlag();
    }
  }, [shouldOpenedReload, resetOpenedReloadFlag]);

  const onChangePage = (newPage: number) => {
    if (newPage !== state.page) {
      setState((prev) => ({ ...prev, page: newPage }));
    }
  };

  const onFiltersChange = (
    filtersEntry: TransferReservationBaseFiltersFormType
  ) => {
    setState((prev) => ({ ...prev, filters: filtersEntry, page: 1 }));
  };

  const onSearchChange = (search: string) => {
    setState((prev) => ({
      ...prev,
      filters: { ...prev.filters, search },
      page: 1
    }));
  };

  const onChangeRowsPerPage = (newRowsPerPage: number) => {
    setState((prev) => ({ ...prev, rowsPerPage: newRowsPerPage, page: 1 }));
  };

  const onSortChange = (direction: string | null, sortBy: string) => {
    setState((prev) => ({
      ...prev,
      sort: {
        sortDir: direction === 'desc' ? 'DESC' : 'ASC',
        sortBy
      },
      page: 1
    }));
  };

  const setColumnVisibility = (
    newColumnVisibility: OpenedTravelColumnVisibility
  ) => {
    setState((prev) => ({ ...prev, columnVisibility: newColumnVisibility }));
  };

  const resetState = () => {
    setState({
      page: 1,
      rowsPerPage: 100,
      sort: getDefaultSort(),
      filters: getDefaultFilters(),
      columnVisibility: getDefaultColumnVisibility()
    });
  };

 

  return {
    openedTravel: {
      data: getOpenedTravel.data?.data || [],
      total: getOpenedTravel.data?.total || 0,
      isLoading: getOpenedTravel.isLoading,
      error: getOpenedTravel.error,
      serverSideOptions: {
        page: state.page - 1,
        count: getOpenedTravel.data?.total || 0,
        rowsPerPage: state.rowsPerPage,
        sortOrder: {
          name: state.sort.sortBy,
          direction: state.sort.sortDir.toLowerCase() as 'asc' | 'desc'
        }
      }
    },
    reload: getOpenedTravel.reload,
    filters: state.filters,
    page: state.page,
    rowsPerPage: state.rowsPerPage,
    columnVisibility: state.columnVisibility,
    setColumnVisibility,
    resetState,
    onChangePage,
    onChangeRowsPerPage,
    onSortChange,
    onFiltersChange,
    onSearchChange
  };
};
